import { type Component, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { cn } from "@/libs/cn";
import type { Plan } from "../api/types";

type PlanComparisonTableProps = {
  plans: Plan[];
};

const BoolCell: Component<{ value: boolean }> = (props) => (
  <span
    class={cn(
      "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold",
      props.value ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400",
    )}
  >
    {props.value ? "✓" : "—"}
  </span>
);

export const PlanComparisonTable: Component<PlanComparisonTableProps> = (
  props,
) => {
  const { t } = useI18n();

  const supportLabel = (level: string): string => {
    switch (level) {
      case "none":
        return t("billing.upgrade.features.support_level_none") as string;
      case "email":
        return t("billing.upgrade.features.support_level_email") as string;
      case "priority":
        return t("billing.upgrade.features.support_level_priority") as string;
      case "dedicated":
        return t("billing.upgrade.features.support_level_dedicated") as string;
      default:
        return level;
    }
  };

  const aiLabel = (value: boolean | "basic"): string => {
    if (value === "basic")
      return t("billing.upgrade.features.ai_matching.basic") as string;
    if (value === true)
      return t("billing.upgrade.features.ai_matching.full") as string;
    return t("billing.upgrade.features.ai_matching.none") as string;
  };

  const formatCount = (value: number): string =>
    value === -1
      ? (t("billing.upgrade.features.unlimited") as string)
      : String(value);

  return (
    <div class="hidden lg:block overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-100 bg-gray-50">
            <th class="px-4 py-3 text-left font-semibold text-gray-500 w-52" />
            <For each={props.plans}>
              {(plan) => (
                <th class="px-4 py-3 text-center font-semibold text-gray-900">
                  {plan.name}
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.max_active_job_posts")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center text-gray-700">
                  {plan.features
                    ? formatCount(plan.features.max_active_job_posts)
                    : "—"}
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.max_applications_per_job")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center text-gray-700">
                  {plan.features
                    ? formatCount(plan.features.max_applications_per_job)
                    : "—"}
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.max_team_members")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center text-gray-700">
                  {plan.features
                    ? formatCount(plan.features.max_team_members)
                    : "—"}
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.ats")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center">
                  <Show
                    when={plan.features}
                    fallback={<span class="text-gray-300">—</span>}
                  >
                    {(features) => <BoolCell value={features().ats} />}
                  </Show>
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.anonymous_cv")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center">
                  <Show
                    when={plan.features}
                    fallback={<span class="text-gray-300">—</span>}
                  >
                    {(features) => (
                      <BoolCell value={features().anonymous_cv === true} />
                    )}
                  </Show>
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.candidate_report")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center">
                  <Show
                    when={plan.features}
                    fallback={<span class="text-gray-300">—</span>}
                  >
                    {(features) => (
                      <BoolCell value={features().candidate_report === true} />
                    )}
                  </Show>
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.ai_matching.label")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center text-gray-700">
                  {plan.features ? aiLabel(plan.features.ai_matching) : "—"}
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.custom_integrations")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center">
                  <Show
                    when={plan.features}
                    fallback={<span class="text-gray-300">—</span>}
                  >
                    {(features) => (
                      <BoolCell value={features().custom_integrations} />
                    )}
                  </Show>
                </td>
              )}
            </For>
          </tr>
          <tr class="hover:bg-gray-50">
            <td class="px-4 py-3 text-gray-600">
              {t("billing.upgrade.features.support_level")}
            </td>
            <For each={props.plans}>
              {(plan) => (
                <td class="px-4 py-3 text-center text-gray-700">
                  {plan.features
                    ? supportLabel(plan.features.support_level)
                    : "—"}
                </td>
              )}
            </For>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
